import { AxiosPromise } from 'axios';
import { instance } from '.';
import { URLS } from './index';
import { Logger } from 'fsts';
import invoiceFunctions, {
  getPaymentStatusText,
  Invoice,
  InvoicePaymentStatus,
  InvoicePeriodType,
  Value,
} from '../model/invoice';
import { SearchParams } from '../model/searchParams';
import ODataFilterBuilder from 'odata-filter-builder';
import { CONST } from '../utils/constants';
import { DefaultBackendHelper } from '../utils/backendHelper';

const logger = new Logger('backend.Invoices');

export interface BackendInvoice {
  getInvoices: (
    searchParams: SearchParams,
    attendeeAppUserId?: string,
    filterByStatuses?: InvoicePaymentStatus[],
    dateFrom?: string,
    dateTill?: string,
    locationId?: string,
    departmentId?: string,
    dueDateBefore?: string,
    invoiceRunId?: string
  ) => AxiosPromise<Value>;
  updateInvoice: (invoice: Invoice) => AxiosPromise<any>;
  exportCompleteInvoices: (
    markExportedInvoicesPaid: boolean,
    newDueDateOnExportedInvoices: undefined | string,
    exportInSingleFile: boolean,
    exportPreviouslyExportedInvoices: boolean,
    exportIncompleteInvoices: boolean,
    exportForMonth: number | undefined,
    exportForYear: number | undefined,
    exportForAttendeeIds: string[] | undefined,
    exportWithPaymentStatuses: InvoicePaymentStatus[] | undefined,
    exportWithPeriodTypes: InvoicePeriodType[] | undefined,
    invoiceRunId: string | undefined,
    culture: string
  ) => AxiosPromise<any>;
  exportSingleInvoice: (
    invoiceId: string,
    culture: string
  ) => AxiosPromise<any>;
  countCompleteNonExportedInvoices: (
    exportPreviouslyExportedInvoices: boolean,
    exportIncompleteInvoices: boolean,
    exportForMonth: number | undefined,
    exportForYear: number | undefined,
    exportForAttendeesOfCourseIds: string[] | undefined,
    exportWithPaymentStatuses: InvoicePaymentStatus[] | undefined,
    exportWithPeriodTypes: InvoicePeriodType[] | undefined,
    invoiceRunId: string | undefined
  ) => AxiosPromise<number>;
  getStartDateOfFollowUpInvoice: (
    periodType: InvoicePeriodType,
    inputDate: string,
    attendeeAppUserId: string
  ) => AxiosPromise<string>;
}

export const DefaultBackendInvoice: BackendInvoice = {
  getInvoices(
    searchParams: SearchParams,
    attendeeAppUserId?: string,
    filterByStatuses?: InvoicePaymentStatus[],
    dateFrom?: string,
    dateTill?: string,
    locationId?: string,
    departmentId?: string,
    dueDateBefore?: string,
    invoiceRunId?: string
  ): AxiosPromise<Value> {
    var odfb = ODataFilterBuilder('and');

    if (attendeeAppUserId !== undefined) {
      odfb.eq(CONST.AttendeeAppUserId, attendeeAppUserId, false);
    }

    if (dateFrom !== undefined) {
      odfb.ge(CONST.PeriodStartDate, dateFrom, false);
    }

    if (filterByStatuses !== undefined && filterByStatuses.length > 0) {
      let odfbStati = ODataFilterBuilder('or');
      filterByStatuses.forEach((x) =>
        odfbStati.eq(CONST.PaymentStatus, getPaymentStatusText(x))
      );
      odfb.and(odfbStati);
    }

    if (dateTill !== undefined) {
      odfb.le(CONST.PeriodStartDate, dateTill, false);
    }

    if (dueDateBefore !== undefined) {
      odfb.le(CONST.DueDate, dueDateBefore, false);
      odfb.ne(CONST.DueDate, null, false);
    }

    if (invoiceRunId !== undefined) {
      odfb.eq(CONST.InvoiceRunId, invoiceRunId, false);
    }

    let baseUrl =
      locationId !== undefined
        ? `${URLS.invoiceOdata}/GetInLocation(locationId=${locationId})`
        : departmentId !== undefined
        ? `${URLS.invoiceOdata}/GetInDepartment(departmentId=${departmentId})`
        : URLS.invoiceOdata;

    let url = DefaultBackendHelper.makeUrl(
      baseUrl,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter,
      odfb
    );

    logger.log(`getInvoices${url}`);
    return instance.get<Value>(url);
  },
  updateInvoice(invoice: Invoice): AxiosPromise<any> {
    return instance.put<Invoice>(
      `${URLS.invoice}/${invoice.id}`,
      invoiceFunctions.toAPI(invoice)
    );
  },
  exportCompleteInvoices(
    markExportedInvoicesPaid: boolean,
    newDueDateOnExportedInvoices: undefined | string,
    exportInSingleFile: boolean,
    exportPreviouslyExportedInvoices: boolean,
    exportIncompleteInvoices: boolean,
    exportForMonth: number | undefined,
    exportForYear: number | undefined,
    exportForAttendeeIds: string[] | undefined,
    exportWithPaymentStatuses: InvoicePaymentStatus[] | undefined,
    exportWithPeriodTypes: InvoicePeriodType[] | undefined,
    invoiceRunId: string | undefined,
    culture: string
  ): AxiosPromise<any> {
    let url = `${URLS.invoice}/ExportCompleteInvoices`;
    url = DefaultBackendHelper.addParamsToUrl(url, [`culture=${culture}`]);
    return instance.post(
      url,
      {
        MarkExportedInvoicesPaid: markExportedInvoicesPaid,
        ExportInSingleFile: exportInSingleFile,
        ExportPreviouslyExportedInvoices: exportPreviouslyExportedInvoices,
        ExportIncompleteInvoices: exportIncompleteInvoices,
        ExportForMonth: exportForMonth,
        ExportForYear: exportForYear,
        ExportForAttendeeIds: exportForAttendeeIds,
        ExportWithPaymentStatuses: exportWithPaymentStatuses,
        ExportWithPeriodTypes: exportWithPeriodTypes,
        InvoiceRunId: invoiceRunId,
        NewDueDateOnExportedInvoices: newDueDateOnExportedInvoices,
      },
      { responseType: 'blob' }
    );
  },
  exportSingleInvoice(invoiceId: string, culture: string): AxiosPromise<any> {
    let url = `${URLS.invoice}/ExportSingleInvoice`;
    url = DefaultBackendHelper.addParamsToUrl(url, [`culture=${culture}`]);
    return instance.post(
      url,
      { InvoiceId: invoiceId },
      { responseType: 'blob' }
    );
  },
  countCompleteNonExportedInvoices(
    exportPreviouslyExportedInvoices: boolean,
    exportIncompleteInvoices: boolean,
    exportForMonth: number | undefined,
    exportForYear: number | undefined,
    exportForAttendeeIds: string[] | undefined,
    exportWithPaymentStatuses: InvoicePaymentStatus[] | undefined,
    exportWithPeriodTypes: InvoicePeriodType[] | undefined,
    invoiceRunId: string | undefined
  ): AxiosPromise<number> {
    const url = `${URLS.invoice}/CountCompleteNonExportedInvoices`;
    return instance.post<number>(url, {
      ExportPreviouslyExportedInvoices: exportPreviouslyExportedInvoices,
      ExportIncompleteInvoices: exportIncompleteInvoices,
      ExportForMonth: exportForMonth,
      ExportForYear: exportForYear,
      ExportForAttendeeIds: exportForAttendeeIds,
      ExportWithPaymentStatuses: exportWithPaymentStatuses,
      ExportWithPeriodTypes: exportWithPeriodTypes,
      InvoiceRunId: invoiceRunId,
    });
  },
  getStartDateOfFollowUpInvoice(
    periodType: InvoicePeriodType,
    inputDate: string,
    attendeeAppUserId: string
  ): AxiosPromise<string> {
    const url = `${URLS.invoice}/GetFollowUpStartDate`;
    return instance.post<string>(url, {
      PeriodType: periodType,
      InputDate: inputDate,
      AttendeeAppUserId: attendeeAppUserId,
    });
  },
};
